import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, Users, Edit2, UserPlus, Calendar, AlertCircle } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { format, addMonths, getDay, addDays } from "date-fns";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StudentEditDialog } from "@/components/students/StudentEditDialog";
import { AddStudentDialog } from "@/components/students/AddStudentDialog";
import { BatchChangeDialog } from "@/components/students/BatchChangeDialog";
import { RenewalDialog } from "@/components/students/RenewalDialog";
import { useEnrollmentForm } from "@/hooks/useEnrollmentForm";
import { ResilientFetchBanner, runWithSettled } from "@/lib/resilientFetch";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";
import { useDebounce } from "@/hooks/useDebounce";
import { getCapacityStatus } from "@/lib/capacity";
import { CapacityWarningDialog } from "@/components/students/CapacityWarningDialog";
import { ContactPickerDialog } from "@/components/shared/ContactPickerDialog";
import { openWhatsApp, buildContactOptions } from "@/lib/contactActions";
import { buildPublicReceiptUrl } from "@/components/receipts/ReceiptViewerModal";

const PAGE_SIZE = 50;

const StudentManagement = ({ user, onLogout }) => {
  const { default_timezone, default_country_code } = useBrand();
  // Feb 2026 enhancement — post-enrollment WhatsApp invite picker state.
  // {batch, student} → opened from the enroll-success toast action when the
  // batch has a `whatsapp_invite_link` AND the student has both phone + parent.
  const [postEnrollInvitePicker, setPostEnrollInvitePicker] = useState(null);
  // Apr 2026 — "batch ended" blocking modal for renewal flow.
  const [batchEndedModal, setBatchEndedModal] = useState(null);
  // Feb 2026 — "No Renewal Required" confirmation modal.
  //   { enrollment, mode: 'mark' | 'unmark' }
  const [noRenewalModal, setNoRenewalModal] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [studios, setStudios] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [masterInstructors, setMasterInstructors] = useState([]);
  const [styles, setStyles] = useState([]);
  const [tenures, setTenures] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [tshirtSizes, setTshirtSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchFailures, setFetchFailures] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 350);
  const [renewalFilter, setRenewalFilter] = useState("all");
  const [renewalUntilDate, setRenewalUntilDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [studentStatusFilter, setStudentStatusFilter] = useState("active");

  // Pagination — backend now serves pages of 50 students.
  const [page, setPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Lazy-loaded "lookup" data — only fetched the first time a dialog needs it
  // (Edit Student / Add Student / Renewal / Batch change). Keeping these off
  // the initial list-page load is the single biggest frontend speed-up.
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  // Capacity warning modal — shared across enroll / change-batch / renew flows.
  // Soft block: enrolled >= capacity triggers a confirm prompt; admin can
  // override and proceed. State carries the deferred callback so the same
  // submit path runs after confirmation.
  const [capacityWarn, setCapacityWarn] = useState(null);

  /**
   * Lightweight refresh of just `/batches` — used after every successful
   * mutation (enroll / change / renew) AND just before the capacity check
   * fires on submit, so the count we compare against is real-time.
   * Always resolves; on failure leaves the existing state alone.
   *
   * Returns the latest batch list so callers can do an in-the-moment lookup
   * without depending on the React state update cycle.
   */
  const refreshBatches = async () => {
    try {
      const res = await api.get("/batches");
      const list = res.data?.batches || [];
      setBatches(list);
      return list;
    } catch {
      return batches;
    }
  };

  /**
   * Run `submitFn` immediately when the destination batch is below capacity.
   * If it's at/over capacity, open the CapacityWarningDialog and only run
   * `submitFn` when the admin clicks "Proceed Anyway".
   *
   * Always re-fetches `/batches` first so the count shown in the warning
   * (and the decision whether to warn at all) is real-time.
   */
  const withCapacityCheck = async (batchId, submitFn, { description } = {}) => {
    const latest = await refreshBatches();
    const batch = latest.find((b) => b.batch_id === batchId);
    if (!batch) return submitFn();
    const cap = getCapacityStatus(batch);
    if (!cap || !cap.is_full) return submitFn();
    setCapacityWarn({
      batchName: batch.name,
      capacity: cap.capacity,
      enrolled: cap.enrolled,
      description,
      onConfirm: submitFn,
    });
  };

  // Edit student dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentEnrollments, setStudentEnrollments] = useState([]);
  const [editTab, setEditTab] = useState("profile");
  const [showRenewedEnrollments, setShowRenewedEnrollments] = useState(false);
  // Bumped every time we successfully record/create a payment for the
  // currently-open student → triggers PaymentHistory to refetch instead of
  // showing the stale list from when the dialog was first opened.
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState(0);

  // Profile edit form
  const [profileForm, setProfileForm] = useState({});

  // Enroll form (managed by hook)
  // Feb 2026 — New-enrollment flow now uses the milestone resolver (same as
  // renewals). Cohort-based enrollments keep the legacy prorated path because
  // cohorts already have their own deterministic session sequence.
  const isMilestoneEnroll = true;
  const {
    form: enrollForm,
    preview: prorationPreview,
    previewLoading,
    activeCohort,
    getPrice,
    handlers: enrollHandlers,
    reset: resetEnrollForm,
    isMilestoneMode: enrollIsMilestoneMode,
  } = useEnrollmentForm({ batches, pricing, mode: "enroll", useMilestoneMode: isMilestoneEnroll });

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    payment_status: "completed",
    payment_method: "cash",
    cheque_bank_name: "",
    cheque_number: "",
    amount_paid: "",
  });

  // Add Student dialog
  const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);
  const [addStudentForm, setAddStudentForm] = useState({
    name: "", phone: "", email: "", gender: "", age: "",
    address: "", date_of_birth: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    tshirt_size_id: "",
  });

  // Batch change dialog
  const [batchChangeDialogOpen, setBatchChangeDialogOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [newBatchId, setNewBatchId] = useState("");
  const [batchChangeWarning, setBatchChangeWarning] = useState(null);

  // Renewal dialog (renewal form managed by hook)
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  // Feb 2026 — Milestone-based renewal engine is now the default for all
  // new renewals. Legacy calendar-month path stays reachable via legacy
  // enrollments that haven't been renewed since the cutover.
  const isMilestoneRenewal = true;
  const {
    form: renewalForm,
    preview: renewalPreview,
    previewLoading: renewalPreviewLoading,
    handlers: renewalHandlers,
    reset: resetRenewalForm,
    isMilestoneMode: renewalIsMilestoneMode,
  } = useEnrollmentForm({
    batches,
    pricing,
    mode: "renew",
    fixedBatchId: selectedEnrollment?.batch_id,
    previousEndDate: selectedEnrollment?.end_date,
    useMilestoneMode: isMilestoneRenewal,
  });

  useEffect(() => {
    // Filters changed → reset to page 1 and refetch.
    setPage(1);
    fetchData(1, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, renewalFilter, renewalUntilDate, studentStatusFilter]);

  // Deep-link: auto-open student profile when student_id is in URL
  useEffect(() => {
    const studentId = searchParams.get("student_id");
    const tab = searchParams.get("tab");
    if (studentId) {
      (async () => {
        try {
          // Deep-link requires lookups (batches/styles/etc.) for the dialog,
          // so kick those off in parallel with the student fetch.
          fetchLookups();
          const res = await api.get(`/users/${studentId}`);
          if (res.data.user) {
            openEditDialog(res.data.user);
            if (tab) setEditTab(tab);
          }
        } catch {
          toast.error("Student not found");
        }
        setSearchParams({}, { replace: true });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------
  // List fetch — only `students` (cheap, paginated). Heavy lookup data
  // (batches/pricing/tenures/styles/instructors/studios/org) is loaded
  // lazily on first dialog open via fetchLookups().
  // ---------------------------------------------------------------
  const buildStudentParams = (pageNum) => {
    const params = new URLSearchParams();
    params.append("role", "student");
    params.append("page", String(pageNum));
    params.append("page_size", String(PAGE_SIZE));
    if (debouncedSearch) params.append("search", debouncedSearch);
    if (studentStatusFilter && studentStatusFilter !== "active") {
      params.append("student_status_filter", studentStatusFilter);
    }
    if (renewalFilter !== "all") {
      params.append("renewal_filter", renewalFilter);
      if (renewalFilter === "due" && renewalUntilDate) {
        params.append("renewal_until_date", renewalUntilDate);
      }
    }
    return params;
  };

  const fetchData = async (pageNum = 1, { append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    const params = buildStudentParams(pageNum);
    const FETCHERS = {
      "students": () => api.get(`/users?${params.toString()}`),
    };

    const { results, failures } = await runWithSettled(FETCHERS);
    if (results["students"]) {
      const data = results["students"].data;
      const incoming = data.users || [];
      setStudents((prev) => (append ? [...prev, ...incoming] : incoming));
      setTotalStudents(data.total ?? incoming.length);
      setHasMore(Boolean(data.has_more));
    }
    setFetchFailures(failures);
    if (append) setLoadingMore(false);
    else setLoading(false);
  };

  const loadMoreStudents = async () => {
    const next = page + 1;
    setPage(next);
    await fetchData(next, { append: true });
  };

  // Fetch the heavy lookup tables exactly once — triggered when the user
  // first interacts with a dialog that needs them.
  const fetchLookups = async () => {
    if (lookupsLoaded || lookupsLoading) return;
    setLookupsLoading(true);
    const FETCHERS = {
      "batches":      () => api.get("/batches"),
      "dance styles": () => api.get("/dance-styles"),
      "tenures":      () => api.get("/tenures"),
      "pricing":      () => api.get("/pricing"),
      "studios":      () => api.get("/studios"),
      "organization": () => api.get("/organization"),
      "instructors":  () => api.get("/users?role=instructor"),
      "masters":      () => api.get("/instructors"),
    };
    const { results, failures } = await runWithSettled(FETCHERS);
    if (results["batches"])      setBatches(results["batches"].data.batches);
    if (results["dance styles"]) setStyles(results["dance styles"].data.styles);
    if (results["tenures"])      setTenures(results["tenures"].data.tenures);
    if (results["pricing"])      setPricing(results["pricing"].data.pricing);
    if (results["studios"])      setStudios(results["studios"].data.studios || []);
    if (results["instructors"])  setInstructors(results["instructors"].data.users || []);
    if (results["masters"])      setMasterInstructors(results["masters"].data.instructors || []);
    if (results["organization"]) {
      const sizes = (results["organization"].data.organization?.tshirt_sizes || []).slice();
      sizes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setTshirtSizes(sizes);
    }
    // Even partial success is enough — UI gracefully no-ops on missing data.
    if (!failures.length || Object.keys(results).length > 0) setLookupsLoaded(true);
    setLookupsLoading(false);
  };

  const retryFailed = async () => {
    if (!fetchFailures.length) return;
    setRetrying(true);
    await fetchData(page, { append: false });
    setRetrying(false);
    if (!fetchFailures.length) toast.success("Refreshed");
  };

  const handleAddStudent = async () => {
    if (!addStudentForm.name.trim() || !addStudentForm.phone.trim()) {
      toast.error("Name and mobile number are required");
      return;
    }
    try {
      await api.post("/users/create-student", addStudentForm);
      toast.success("Student added successfully");
      setAddStudentDialogOpen(false);
      setAddStudentForm({ name: "", phone: "", email: "", gender: "", age: "", address: "", date_of_birth: "", emergency_contact_name: "", emergency_contact_phone: "", tshirt_size_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add student");
    }
  };

  const openEditDialog = async (student) => {
    setSelectedStudent(student);
    // Kick off lookups in parallel — dialog renders immediately with cached
    // (or empty) data and re-renders as lookups arrive.
    fetchLookups();
    // Rule 3 — entity fetch on modal open (narrow scope). Re-fetch this single
    // student so the dialog never opens on a stale list-cached row. Lookups
    // follow stale-while-revalidate above; the entity itself is always fresh.
    let entity = student;
    try {
      const res = await api.get(`/users/${student.user_id}`);
      if (res?.data?.user) {
        entity = res.data.user;
        setSelectedStudent(entity);
        // Also patch the row in the list cache so the next click on the same
        // card opens with the up-to-date row even before the next fetchData.
        setStudents((prev) =>
          prev.map((s) => (s.user_id === entity.user_id ? { ...s, ...entity } : s))
        );
      }
    } catch {
      // Network blip — fall back to the list row; the open dialog is still safe.
    }
    const fullName = entity.name || [entity.first_name, entity.last_name].filter(Boolean).join(" ").trim();
    setProfileForm({
      name: fullName,
      age: entity.age ?? "",
      gender: entity.gender ?? "",
      email: entity.email ?? "",
      phone: entity.phone ?? "",
      address: entity.address ?? "",
      date_of_birth: entity.date_of_birth ?? "",
      parent_name: entity.parent_name ?? "",
      parent_phone: entity.parent_phone ?? "",
      tshirt_size_id: entity.tshirt_size_id ?? "",
    });
    setEditTab("profile");
    setEditDialogOpen(true);

    try {
      const res = await api.get(`/enrollments?student_id=${entity.user_id}&include_batch_details=true`);
      setStudentEnrollments(res.data.enrollments);
    } catch (error) {
      console.error("Failed to load enrollments");
    }
  };

  const updateProfile = async () => {
    try {
      const payload = { ...profileForm };
      if (payload.age) payload.age = parseInt(payload.age) || null;
      if (payload.date_of_birth === "") delete payload.date_of_birth;
      const res = await api.put(`/users/${selectedStudent.user_id}`, payload);
      const updated = res?.data?.user;
      // Rule 1 — backend response is the source of truth. Patch local state
      // immediately so a fast re-open of the same profile cannot read a stale
      // row from `students[]` (the bug that surfaced as "T-shirt size reset
      // to default after save" when the list refresh was slow).
      if (updated) {
        setSelectedStudent(updated);
        setStudents((prev) =>
          prev.map((s) => (s.user_id === updated.user_id ? { ...s, ...updated } : s))
        );
        // Re-hydrate the open form from the authoritative response so any
        // server-side normalisation (e.g. name → first/last split) is reflected.
        setProfileForm((prevForm) => ({
          ...prevForm,
          name: updated.name ?? prevForm.name,
          age: updated.age ?? "",
          gender: updated.gender ?? "",
          email: updated.email ?? "",
          phone: updated.phone ?? "",
          address: updated.address ?? "",
          date_of_birth: updated.date_of_birth ?? "",
          parent_name: updated.parent_name ?? "",
          parent_phone: updated.parent_phone ?? "",
          tshirt_size_id: updated.tshirt_size_id ?? "",
        }));
      }
      toast.success("Profile updated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    }
  };

  const enrollStudent = async () => {
    // Feb 2026 — milestone mode: require batch + start_date + (tenure_id OR session_count)
    // Legacy cohort mode still requires tenure_id.
    const usingMilestone = enrollIsMilestoneMode && !activeCohort;
    if (!enrollForm.batch_id || !enrollForm.start_date) {
      toast.error("Please fill all required fields");
      return;
    }
    if (usingMilestone) {
      if (enrollForm.renewal_mode === "tenure" && !enrollForm.tenure_id) {
        toast.error("Please select a tenure");
        return;
      }
      if (enrollForm.renewal_mode === "session") {
        const sc = parseInt(enrollForm.session_count);
        if (!sc || sc <= 0) {
          toast.error("Please enter number of sessions");
          return;
        }
      }
    } else if (!enrollForm.tenure_id) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!prorationPreview) {
      toast.error("Proration preview not available. Please wait or re-select dates.");
      return;
    }
    const batch = batches.find(b => b.batch_id === enrollForm.batch_id);
    // Legacy path still needs a resolved price row (pricing_id required by
    // POST /enrollments); milestone path derives fee via the resolver.
    let price = null;
    if (!usingMilestone) {
      price = getPrice(batch.style_id, enrollForm.tenure_id);
      if (!price) {
        toast.error("No pricing found for this style and tenure combination");
        return;
      }
    }
    // Capacity soft-block — admin can override.
    withCapacityCheck(enrollForm.batch_id, () => _doEnroll(batch, price, usingMilestone), {
      description: "Enrolling this student will exceed the configured capacity.",
    });
  };

  const _doEnroll = async (batch, price, usingMilestone) => {
    // Milestone preview returns `full_fee` + `prorated_fee` — pick based on toggle.
    // Legacy uses `total_payable` / `enrollment_fee`.
    let totalAmount;
    if (usingMilestone) {
      if (enrollForm.renewal_mode === "session") {
        totalAmount = prorationPreview.session_fee ?? prorationPreview.full_fee ?? prorationPreview.final_fee ?? 0;
      } else {
        const useProrated = !!enrollForm.is_prorated && !!prorationPreview.is_prorated_available;
        totalAmount = useProrated
          ? (prorationPreview.prorated_fee ?? 0)
          : (prorationPreview.full_fee ?? 0);
      }
    } else {
      totalAmount = prorationPreview.total_payable || prorationPreview.enrollment_fee;
    }
    let amountPaid = totalAmount;
    let balanceAmount = 0;
    let paymentStatus = paymentForm.payment_status;

    if (paymentForm.payment_status === "partial") {
      amountPaid = parseFloat(paymentForm.amount_paid) || 0;
      balanceAmount = totalAmount - amountPaid;
      if (balanceAmount < 0) balanceAmount = 0;
    } else if (paymentForm.payment_status === "pending") {
      amountPaid = 0;
      balanceAmount = totalAmount;
    }

    try {
      const enrollPayload = {
        student_id: selectedStudent.user_id,
        batch_id: enrollForm.batch_id,
        start_date: new Date(enrollForm.start_date).toISOString(),
      };
      if (usingMilestone) {
        // Milestone mode — backend resolver computes fee, class count,
        // renewal_date. `is_prorated` is only meaningful for tenure mode.
        enrollPayload.renewal_mode = enrollForm.renewal_mode || "tenure";
        if (enrollForm.renewal_mode === "session") {
          enrollPayload.session_count = parseInt(enrollForm.session_count);
        } else {
          enrollPayload.tenure_id = enrollForm.tenure_id;
          // Only allow prorated if resolver said it's applicable.
          enrollPayload.is_prorated =
            !!enrollForm.is_prorated && !!prorationPreview.is_prorated_available;
        }
      } else {
        enrollPayload.pricing_id = price.pricing_id;
        enrollPayload.charge_type = enrollForm.charge_type || "full";
      }
      if (enrollForm.discount_type) enrollPayload.discount_type = enrollForm.discount_type;
      if (enrollForm.discount_value !== "" && enrollForm.discount_value !== undefined) {
        enrollPayload.discount_value = parseFloat(enrollForm.discount_value) || 0;
      }
      if (enrollForm.cohort_id) {
        enrollPayload.cohort_id = enrollForm.cohort_id;
      }
      const enrollmentRes = await api.post("/enrollments", enrollPayload);

      // Capture the receipt from the /payments response. Backend generates
      // it inline whenever `amount_paid > 0` and returns it on the same
      // request — zero extra round-trip.
      const paymentRes = await api.post("/payments", {
        enrollment_id: enrollmentRes.data.enrollment.enrollment_id,
        student_id: selectedStudent.user_id,
        total_amount: totalAmount,
        amount_paid: amountPaid,
        balance_amount: balanceAmount,
        amount: totalAmount,
        payment_method: paymentForm.payment_status !== "pending" ? paymentForm.payment_method : "pending",
        payment_status: paymentStatus,
        payment_date: paymentForm.payment_status !== "pending" ? new Date().toISOString() : null,
        cheque_bank_name: paymentForm.payment_method === "cheque" ? paymentForm.cheque_bank_name : null,
        cheque_number: paymentForm.payment_method === "cheque" ? paymentForm.cheque_number : null,
      });
      const issuedReceipt = paymentRes?.data?.receipt || null;

      // Success — show toast immediately so the admin knows the create completed.
      toast.success("Student enrolled successfully!");

      // Feb 2026 enhancement: auto-open recipient picker after enrollment when
      // EITHER a WhatsApp invite link OR a payment receipt is available (and
      // we have ≥1 valid contact). The picker carries both links; the message
      // template gracefully drops whichever line is missing.
      const inviteLink = batch?.whatsapp_invite_link || "";
      const receiptLink = issuedReceipt ? buildPublicReceiptUrl(issuedReceipt) : "";

      if (inviteLink || receiptLink) {
        const inviteOptions = buildContactOptions({
          studentName: selectedStudent?.name,
          studentPhone: selectedStudent?.phone,
          guardianName: selectedStudent?.parent_name || "Guardian",
          guardianPhone: selectedStudent?.parent_phone,
        });
        if (inviteOptions.length > 0) {
          setPostEnrollInvitePicker({
            mode: "enrollment",
            batch,
            student: selectedStudent,
            inviteLink,
            receiptLink,
          });
        }
      }

      // Force PaymentHistory to refetch so the new pending Payment row
      // appears in the Payments tab when the admin switches to it.
      setPaymentsRefreshKey((k) => k + 1);
      resetEnrollForm();
      setPaymentForm({ payment_status: "completed", payment_method: "cash", cheque_bank_name: "", cheque_number: "", amount_paid: "" });
      setEditTab("enrollments");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enroll student");
      return; // Don't refresh below if the create itself failed.
    }

    // Refresh the enrollments list. Wrapped in its own try/catch — a transient
    // refresh failure must NOT raise a "Failed to enroll" error after we've
    // already toasted success and the record exists in the DB.
    try {
      const res = await api.get(`/enrollments?student_id=${selectedStudent.user_id}&include_batch_details=true`);
      setStudentEnrollments(res.data.enrollments);
    } catch (refreshError) {
      // eslint-disable-next-line no-console
      console.warn("Enrollment created, but failed to refresh list:", refreshError);
    }
    // Keep batch occupancy counts real-time — next dropdown opening sees fresh data.
    refreshBatches();
  };

  const openBatchChangeDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setNewBatchId("");
    setBatchChangeWarning(null);
    setBatchChangeDialogOpen(true);
  };

  const checkBatchChange = (newBatchId) => {
    setNewBatchId(newBatchId);
    if (!selectedEnrollment || !newBatchId) {
      setBatchChangeWarning(null);
      return;
    }
    const currentBatch = batches.find(b => b.batch_id === selectedEnrollment.batch_id);
    const newBatch = batches.find(b => b.batch_id === newBatchId);
    if (!currentBatch || !newBatch) return;

    const warnings = [];
    if (currentBatch.style_id !== newBatch.style_id) {
      const currentStyle = styles.find(s => s.style_id === currentBatch.style_id);
      const newStyle = styles.find(s => s.style_id === newBatch.style_id);
      warnings.push(`Dance style will change from "${currentStyle?.name}" to "${newStyle?.name}"`);
    }
    const currentPrice = getPrice(currentBatch.style_id, selectedEnrollment.pricing?.tenure_id);
    const newPrice = getPrice(newBatch.style_id, selectedEnrollment.pricing?.tenure_id);
    if (currentPrice?.price !== newPrice?.price) {
      warnings.push(`Pricing may differ for the new batch (Current: ₹${currentPrice?.price || 'N/A'}, New: ₹${newPrice?.price || 'N/A'})`);
    }
    setBatchChangeWarning(warnings.length > 0 ? warnings : null);
  };

  const confirmBatchChange = async () => {
    if (!selectedEnrollment || !newBatchId) return;
    withCapacityCheck(newBatchId, async () => {
      try {
        await api.put(`/enrollments/${selectedEnrollment.enrollment_id}`, { batch_id: newBatchId });
        toast.success("Batch changed successfully");
        setBatchChangeDialogOpen(false);
        const res = await api.get(`/enrollments?student_id=${selectedStudent.user_id}&include_batch_details=true`);
        setStudentEnrollments(res.data.enrollments);
        refreshBatches();

        // Feb 2026 enhancement: trigger the same WhatsApp invite picker for
        // Batch Change (Q2=b) so the admin can send the destination batch's
        // group invite without leaving the page.
        const destinationBatch = batches.find((b) => b.batch_id === newBatchId);
        if (destinationBatch?.whatsapp_invite_link) {
          const opts = buildContactOptions({
            studentName: selectedStudent?.name,
            studentPhone: selectedStudent?.phone,
            guardianName: selectedStudent?.parent_name || "Guardian",
            guardianPhone: selectedStudent?.parent_phone,
          });
          if (opts.length > 0) {
            setPostEnrollInvitePicker({
              mode: "enrollment",
              batch: destinationBatch,
              student: selectedStudent,
              inviteLink: destinationBatch.whatsapp_invite_link,
              receiptLink: "",  // Batch change does NOT generate a new receipt.
            });
          }
        }
      } catch (error) {
        toast.error("Failed to change batch");
      }
    }, { description: "Transferring this student will exceed the destination batch's capacity." });
  };

  const openRenewalDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    resetRenewalForm();
    setPaymentForm({ payment_status: "completed", payment_method: "cash", cheque_bank_name: "", cheque_number: "" });
    setRenewalDialogOpen(true);
  };

  // Feb 2026 — "No Renewal Required" flow. Suppresses renewal tracking for
  // a single enrollment without discontinuing the student. Reversible via
  // resumeRenewalTracking().
  const markNoRenewalRequired = (enrollment) => {
    setNoRenewalModal({ enrollment, mode: "mark" });
  };

  const resumeRenewalTracking = (enrollment) => {
    setNoRenewalModal({ enrollment, mode: "unmark" });
  };

  const confirmNoRenewalAction = async () => {
    if (!noRenewalModal?.enrollment) return;
    const { enrollment, mode } = noRenewalModal;
    const endpoint = mode === "mark"
      ? `/enrollments/${enrollment.enrollment_id}/mark-no-renewal`
      : `/enrollments/${enrollment.enrollment_id}/unmark-no-renewal`;
    try {
      await api.post(endpoint, {});
      toast.success(
        mode === "mark"
          ? "Marked as No Renewal Required."
          : "Renewal tracking resumed."
      );
      setNoRenewalModal(null);
      // Close the batch-ended modal too (entry point #2: auto-suggestion).
      setBatchEndedModal(null);
      // Refresh enrollment list + students list so all renewal surfaces update.
      if (selectedStudent) {
        const res = await api.get(
          `/enrollments?student_id=${selectedStudent.user_id}&include_batch_details=true`
        );
        setStudentEnrollments(res.data.enrollments || []);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update enrollment");
    }
  };

  const processRenewal = async () => {
    if (!renewalForm.start_date) {
      toast.error("Please pick a renewal start date");
      return;
    }
    if (isMilestoneRenewal) {
      if (renewalForm.renewal_mode === "tenure" && !renewalForm.tenure_id) {
        toast.error("Please select a tenure");
        return;
      }
      if (renewalForm.renewal_mode === "session" && !(renewalForm.session_count && parseInt(renewalForm.session_count) > 0)) {
        toast.error("Please enter a session count");
        return;
      }
    } else if (!renewalForm.tenure_id) {
      toast.error("Please select a tenure");
      return;
    }
    if (!renewalPreview) {
      toast.error("Preview not available. Please wait or re-select dates.");
      return;
    }
    const batch = batches.find(b => b.batch_id === selectedEnrollment.batch_id);
    if (!isMilestoneRenewal) {
      const price = getPrice(batch.style_id, renewalForm.tenure_id);
      if (!price) {
        toast.error("No pricing found");
        return;
      }
      withCapacityCheck(selectedEnrollment.batch_id, () => _doRenewal(batch, price), {
        description: "This batch is already at full capacity. Renewing the student into it will keep it over the limit.",
      });
      return;
    }
    // Milestone-mode renewal — batch capacity check still applies.
    withCapacityCheck(selectedEnrollment.batch_id, () => _doRenewal(batch, null), {
      description: "This batch is already at full capacity. Renewing the student into it will keep it over the limit.",
    });
  };

  const _doRenewal = async (batch, price) => {
    // Extract the FINAL payable from whichever preview shape came back.
    const totalAmount = isMilestoneRenewal
      ? (renewalPreview.renewal_mode === "session"
           ? (renewalPreview.session_fee ?? 0)
           : (renewalForm.charge_type === "prorated" ? renewalPreview.prorated_fee : renewalPreview.full_fee))
      : (renewalPreview.final_fee ?? renewalPreview.total_payable ?? renewalPreview.enrollment_fee);
    let amountPaid = totalAmount;
    let balanceAmount = 0;
    let paymentStatus = paymentForm.payment_status;

    if (paymentForm.payment_status === "partial") {
      amountPaid = parseFloat(paymentForm.amount_paid) || 0;
      balanceAmount = totalAmount - amountPaid;
      if (balanceAmount < 0) balanceAmount = 0;
    } else if (paymentForm.payment_status === "pending") {
      amountPaid = 0;
      balanceAmount = totalAmount;
    }

    try {
      const renewPayload = {
        start_date: new Date(renewalForm.start_date).toISOString(),
      };
      if (isMilestoneRenewal) {
        renewPayload.renewal_mode = renewalForm.renewal_mode;
        if (renewalForm.renewal_mode === "tenure") {
          renewPayload.tenure_id = renewalForm.tenure_id;
          renewPayload.is_prorated = renewalForm.charge_type === "prorated";
        } else {
          renewPayload.session_count = parseInt(renewalForm.session_count) || 0;
        }
      } else {
        renewPayload.pricing_id = price.pricing_id;
        renewPayload.charge_type = renewalForm.charge_type || "full";
      }
      if (renewalForm.discount_type) renewPayload.discount_type = renewalForm.discount_type;
      if (renewalForm.discount_value !== "" && renewalForm.discount_value !== undefined) {
        renewPayload.discount_value = parseFloat(renewalForm.discount_value) || 0;
      }
      const renewalRes = await api.post(`/enrollments/${selectedEnrollment.enrollment_id}/renew`, renewPayload);

      const paymentRes = await api.post("/payments", {
        enrollment_id: renewalRes.data.enrollment.enrollment_id,
        student_id: selectedStudent.user_id,
        total_amount: totalAmount,
        amount_paid: amountPaid,
        balance_amount: balanceAmount,
        amount: totalAmount,
        payment_method: paymentForm.payment_status !== "pending" ? paymentForm.payment_method : "pending",
        payment_status: paymentStatus,
        payment_date: paymentForm.payment_status !== "pending" ? new Date().toISOString() : null,
        cheque_bank_name: paymentForm.payment_method === "cheque" ? paymentForm.cheque_bank_name : null,
        cheque_number: paymentForm.payment_method === "cheque" ? paymentForm.cheque_number : null,
      });
      const issuedReceipt = paymentRes?.data?.receipt || null;

      toast.success("Subscription renewed successfully!");
      setRenewalDialogOpen(false);
      resetRenewalForm();
      const res = await api.get(`/enrollments?student_id=${selectedStudent.user_id}&include_batch_details=true`);
      setStudentEnrollments(res.data.enrollments);
      fetchData();
      refreshBatches();

      // Feb 2026 enhancement: open the recipient picker ONLY when a receipt
      // was actually issued. Pending-payment renewals silently skip the
      // popup (per spec — no popup, no message). Single-contact case still
      // surfaces the picker so the admin gets an explicit Send / Skip
      // decision (consistent with Q1=a for the enrollment flow).
      if (issuedReceipt) {
        const receiptLink = buildPublicReceiptUrl(issuedReceipt);
        const opts = buildContactOptions({
          studentName: selectedStudent?.name,
          studentPhone: selectedStudent?.phone,
          guardianName: selectedStudent?.parent_name || "Guardian",
          guardianPhone: selectedStudent?.parent_phone,
        });
        if (opts.length > 0) {
          setPostEnrollInvitePicker({
            mode: "renewal",
            batch: batch,
            student: selectedStudent,
            inviteLink: "",  // Renewal message intentionally omits invite link.
            receiptLink,
          });
        }
      }
    } catch (error) {
      // Apr 2026 — surface "batch ended" as a blocking modal instead of a
      // toast. The structured 400 carries batch_id + batch_name + end date so
      // the modal can offer a 1-click "Go to Batch" CTA.
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "batch_ended") {
        setBatchEndedModal({
          batch_id: detail.batch_id,
          batch_name: detail.batch_name || "this batch",
          batch_end_date: detail.batch_end_date,
        });
        return;
      }
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to renew subscription");
      toast.error(msg);
    }
  };

  const getRenewalBadge = (status) => {
    if (status === "overdue") {
      return <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-medium">Renewal Overdue</span>;
    }
    if (status === "due") {
      return <span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">Renewal Due</span>;
    }
    return null;
  };

  const handleDiscontinue = async () => {
    if (!selectedStudent) return;
    if (!window.confirm(`Are you sure you want to discontinue ${selectedStudent.name}? This will expire all their active enrollments.`)) return;
    try {
      await api.put(`/users/${selectedStudent.user_id}/discontinue`);
      toast.success(`${selectedStudent.name} has been discontinued`);
      setEditDialogOpen(false);
      fetchData();
      refreshBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to discontinue student");
    }
  };

  const handleRejoin = async () => {
    if (!selectedStudent) return;
    if (!window.confirm(`Reactivate ${selectedStudent.name}? They can then be enrolled in new classes.`)) return;
    try {
      const res = await api.put(`/users/${selectedStudent.user_id}/rejoin`);
      toast.success(`${selectedStudent.name} has been reactivated`);
      setSelectedStudent(res.data.user);
      fetchData();
      refreshBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rejoin student");
    }
  };

  const getStatusBadge = (student) => {
    if (student.student_status === "discontinued") {
      return <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-xs font-medium">Discontinued</span>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />

      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="student-management-title">
              Students
            </h1>
            <p className="text-sm text-slate-600 mt-1">Manage students, enrollments & subscriptions</p>
          </div>
          <Button
            data-testid="add-student-btn"
            onClick={() => { fetchLookups(); setAddStudentDialogOpen(true); }}
            className="rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Add Student
          </Button>
        </div>

        {fetchFailures.length > 0 && (
          <ResilientFetchBanner
            failedLabels={fetchFailures.map((f) => f.label)}
            onRetry={retryFailed}
            retrying={retrying}
            className="mb-4"
            testid="students-fetch-error"
          />
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Search name or mobile..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-10 sm:h-12 rounded-xl text-sm"
              />
            </div>
            <div className="flex gap-2">
              <select
                data-testid="renewal-filter"
                value={renewalFilter}
                onChange={(e) => setRenewalFilter(e.target.value)}
                className="h-10 sm:h-12 rounded-xl border border-slate-200 px-3 bg-white flex-1 text-sm"
              >
                <option value="all">All</option>
                <option value="due">Renewal Due</option>
                <option value="overdue">Overdue</option>
              </select>
              <select
                data-testid="student-status-filter"
                value={studentStatusFilter}
                onChange={(e) => setStudentStatusFilter(e.target.value)}
                className="h-10 sm:h-12 rounded-xl border border-slate-200 px-3 bg-white flex-1 text-sm"
              >
                <option value="active">Active</option>
                <option value="discontinued">Discontinued</option>
                <option value="all">All</option>
              </select>
            </div>
            {renewalFilter === "due" && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 whitespace-nowrap">Until:</span>
                <Input
                  type="date"
                  data-testid="renewal-until-date"
                  value={renewalUntilDate}
                  onChange={(e) => setRenewalUntilDate(e.target.value)}
                  min={format(new Date(), "yyyy-MM-dd")}
                  className="h-12 rounded-xl w-44"
                />
              </div>
            )}
          </div>
          {renewalFilter === "due" && (
            <p className="text-sm text-slate-500 mt-2">
              Showing students whose subscription ends between today and {fmt(renewalUntilDate, "dd MMM yyyy", default_timezone)}
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : students.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No students found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <h2 className="text-xl font-bold font-heading text-slate-900 mb-4">
              Students ({totalStudents || students.length})
            </h2>
            <div className="space-y-3">
              {students.map((student, index) => (
                <motion.div
                  key={student.user_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(index, 20) * 0.02 }}
                  data-testid={`student-card-${index}`}
                  className="p-4 bg-slate-50 rounded-xl flex items-center justify-between hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => openEditDialog(student)}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900 truncate">{student.name}</p>
                        {getStatusBadge(student)}
                        {getRenewalBadge(student.renewal_status)}
                      </div>
                      <p className="text-sm text-slate-600">{student.phone}</p>
                      {student.subscription_end_date && (
                        <p className={`text-xs mt-1 flex items-center gap-1 ${
                          student.renewal_status === "overdue" ? "text-red-600" :
                          student.renewal_status === "due" ? "text-orange-600" : "text-slate-500"
                        }`}>
                          <Calendar className="w-3 h-3" />
                          {student.renewal_status === "overdue" ? "Expired: " : "Renews: "}
                          {fmt(student.subscription_end_date, "dd MMM yyyy", default_timezone)}
                        </p>
                      )}
                      {student.active_enrollments_count > 0 && (
                        <p className="text-xs text-slate-400">
                          {student.active_enrollments_count} active {student.active_enrollments_count === 1 ? "class" : "classes"}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    data-testid={`edit-student-${index}`}
                    size="sm"
                    className="rounded-full bg-primary hover:bg-primary/90 flex-shrink-0 w-9 h-9 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(student);
                    }}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  data-testid="load-more-students-btn"
                  variant="outline"
                  onClick={loadMoreStudents}
                  disabled={loadingMore}
                  className="rounded-full"
                >
                  {loadingMore ? "Loading..." : `Load more (${totalStudents - students.length} remaining)`}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <StudentEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        selectedStudent={selectedStudent}
        editTab={editTab}
        setEditTab={setEditTab}
        profileForm={profileForm}
        setProfileForm={setProfileForm}
        tshirtSizes={tshirtSizes}
        lookupsLoading={lookupsLoading}
        onUpdateProfile={updateProfile}
        onDiscontinue={handleDiscontinue}
        onRejoin={handleRejoin}
        studentEnrollments={studentEnrollments}
        showRenewedEnrollments={showRenewedEnrollments}
        setShowRenewedEnrollments={setShowRenewedEnrollments}
        onOpenBatchChange={openBatchChangeDialog}
        onOpenRenewal={openRenewalDialog}
        onMarkNoRenewal={markNoRenewalRequired}
        onUnmarkNoRenewal={resumeRenewalTracking}
        onDeleteEnrollment={async (enrollment) => {
          const cls = batches.find((b) => b.batch_id === enrollment.batch_id)?.name || "this batch";
          const status = (enrollment.payment_status || "").toLowerCase();
          const warn = status === "paid"
            ? "This enrollment has a paid payment which will also be deleted. Continue?"
            : `Delete enrollment for ${cls}? Any linked payments will be removed too.`;
          if (!window.confirm(warn)) return;
          try {
            const res = await api.delete(`/enrollments/${enrollment.enrollment_id}`);
            toast.success(`Enrollment removed. ${res.data?.deleted_payments || 0} payment(s) also deleted.`);
            await api.get(`/enrollments?student_id=${selectedStudent.user_id}&include_batch_details=true`)
              .then((r) => setStudentEnrollments(r.data.enrollments || []))
              .catch(() => {});
            await fetchData();
            refreshBatches();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete enrollment");
          }
        }}
        enrollForm={enrollForm}
        batches={batches}
        styles={styles}
        studios={studios}
        instructors={instructors}
        masterInstructors={masterInstructors}
        tenures={tenures}
        pricing={pricing}
        prorationPreview={prorationPreview}
        previewLoading={previewLoading}
        activeCohort={activeCohort}
        isMilestoneMode={enrollIsMilestoneMode && !activeCohort}
        onBatchSelect={enrollHandlers.setBatch}
        onTenureChange={enrollHandlers.setTenure}
        onStartDateChange={enrollHandlers.setStartDate}
        onChargeTypeChange={enrollHandlers.setChargeType}
        onDiscountTypeChange={enrollHandlers.setDiscountType}
        onDiscountValueChange={enrollHandlers.setDiscountValue}
        onRenewalModeChange={enrollHandlers.setRenewalMode}
        onSessionCountChange={enrollHandlers.setSessionCount}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        onEnrollSubmit={enrollStudent}
        paymentsRefreshKey={paymentsRefreshKey}
      />

      <BatchChangeDialog
        open={batchChangeDialogOpen}
        onOpenChange={setBatchChangeDialogOpen}
        selectedEnrollment={selectedEnrollment}
        batches={batches}
        styles={styles}
        studios={studios}
        instructors={instructors}
        masterInstructors={masterInstructors}
        newBatchId={newBatchId}
        onCheckBatchChange={checkBatchChange}
        onConfirm={confirmBatchChange}
        batchChangeWarning={batchChangeWarning}
      />

      <RenewalDialog
        open={renewalDialogOpen}
        onOpenChange={(open) => { setRenewalDialogOpen(open); if (!open) resetRenewalForm(); }}
        selectedEnrollment={selectedEnrollment}
        tenures={tenures}
        pricing={pricing}
        batches={batches}
        renewalForm={renewalForm}
        renewalPreview={renewalPreview}
        renewalPreviewLoading={renewalPreviewLoading}
        onTenureChange={renewalHandlers.setTenure}
        onStartDateChange={renewalHandlers.setStartDate}
        onChargeTypeChange={renewalHandlers.setChargeType}
        onDiscountTypeChange={renewalHandlers.setDiscountType}
        onDiscountValueChange={renewalHandlers.setDiscountValue}
        onRenewalModeChange={renewalHandlers.setRenewalMode}
        onSessionCountChange={renewalHandlers.setSessionCount}
        isMilestoneMode={renewalIsMilestoneMode}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        onSubmit={processRenewal}
        onMarkNoRenewal={(enrollment) => {
          // Close renewal dialog first to avoid stacked modals on iOS Safari.
          setRenewalDialogOpen(false);
          setNoRenewalModal({ enrollment, mode: "mark" });
        }}
      />

      <BottomNav user={user} onLogout={onLogout} />

      <AddStudentDialog
        open={addStudentDialogOpen}
        onOpenChange={setAddStudentDialogOpen}
        form={addStudentForm}
        setForm={setAddStudentForm}
        tshirtSizes={tshirtSizes}
        onSubmit={handleAddStudent}
      />

      <CapacityWarningDialog
        open={!!capacityWarn}
        onOpenChange={(o) => { if (!o) setCapacityWarn(null); }}
        batchName={capacityWarn?.batchName}
        capacity={capacityWarn?.capacity}
        enrolled={capacityWarn?.enrolled}
        description={capacityWarn?.description}
        onConfirm={() => {
          const fn = capacityWarn?.onConfirm;
          setCapacityWarn(null);
          fn?.();
        }}
      />

      {/* Apr 2026 — Batch Ended blocking modal (renewal guard). */}
      <Dialog open={!!batchEndedModal} onOpenChange={(o) => !o && setBatchEndedModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Batch Ended
            </DialogTitle>
          </DialogHeader>
          {batchEndedModal && (
            <div className="space-y-3 text-sm text-slate-700">
              <p>This batch has already ended.</p>
              <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Batch</p>
                  <p className="font-semibold text-slate-800 mt-0.5" data-testid="batch-ended-name">
                    {batchEndedModal.batch_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Ended on</p>
                  <p className="font-semibold text-slate-800 mt-0.5" data-testid="batch-ended-date">
                    {batchEndedModal.batch_end_date
                      ? format(new Date(batchEndedModal.batch_end_date), "dd MMM yyyy")
                      : "—"}
                  </p>
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-slate-600">
                <p className="font-medium text-slate-700">You can:</p>
                <ul className="list-disc list-inside space-y-0.5 pl-1">
                  <li>Extend the batch dates and then renew, or</li>
                  <li>Mark this enrollment as <span className="font-medium">No Renewal Required</span> (workshops / one-time programs).</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              data-testid="batch-ended-cancel"
              onClick={() => setBatchEndedModal(null)}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              data-testid="batch-ended-no-renewal"
              onClick={() => {
                // Auto-suggestion path: ask the user to confirm marking the
                // current enrollment as No Renewal Required.
                if (selectedEnrollment) {
                  setNoRenewalModal({ enrollment: selectedEnrollment, mode: "mark" });
                }
              }}
              className="text-slate-700 border-slate-300 hover:bg-slate-100"
            >
              No Renewal Required
            </Button>
            <Button
              data-testid="batch-ended-go-to-batch"
              onClick={() => {
                const bid = batchEndedModal?.batch_id;
                setBatchEndedModal(null);
                // ClassManagement already listens for `editBatchId` and
                // auto-opens the edit dialog — saves 2-3 clicks for admin.
                if (bid) navigate(`/classes?editBatchId=${bid}`);
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              Go to Batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — "No Renewal Required" confirmation modal.
          Triggered from: (a) enrollment-card button, (b) batch-ended modal
          auto-suggestion, (c) Renewal Dialog secondary action. Reversible. */}
      <Dialog open={!!noRenewalModal} onOpenChange={(o) => !o && setNoRenewalModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {noRenewalModal?.mode === "unmark"
                ? "Resume Renewal Tracking?"
                : "Mark as No Renewal Required?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            {noRenewalModal?.mode === "unmark" ? (
              <>
                <p>This enrollment will resume appearing in renewal lists.</p>
                <p className="text-xs text-slate-500">
                  Use this if the workshop / program is being extended or
                  re-classified as ongoing.
                </p>
              </>
            ) : (
              <>
                <p>
                  This enrollment will be marked as completed and removed from
                  renewal tracking.
                </p>
                <p>The student will remain active in TDAS.</p>
                <p className="text-xs text-slate-500">Continue?</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="no-renewal-cancel"
              onClick={() => setNoRenewalModal(null)}
            >
              Cancel
            </Button>
            <Button
              data-testid="no-renewal-confirm"
              onClick={confirmNoRenewalAction}
              className="bg-slate-800 hover:bg-slate-900 text-white"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — post-enrollment / post-batch-change / post-renewal
          WhatsApp picker. The state's `mode` field selects between the
          enrollment template (invite + receipt, with graceful single-link
          fallbacks) and the renewal template (receipt only). Closing the
          dialog silently skips — no message is sent. */}
      <ContactPickerDialog
        open={!!postEnrollInvitePicker}
        onOpenChange={(o) => { if (!o) setPostEnrollInvitePicker(null); }}
        title={postEnrollInvitePicker?.mode === "renewal" ? "Send renewal receipt?" : "Send WhatsApp invite?"}
        description="Who would you like to message?"
        options={postEnrollInvitePicker ? buildContactOptions({
          studentName: postEnrollInvitePicker.student?.name,
          studentPhone: postEnrollInvitePicker.student?.phone,
          guardianName: postEnrollInvitePicker.student?.parent_name || "Guardian",
          guardianPhone: postEnrollInvitePicker.student?.parent_phone,
        }) : []}
        onPick={(recipientKey) => {
          const ctx = postEnrollInvitePicker;
          if (!ctx?.student) return;
          const phone = recipientKey === "student" ? ctx.student.phone : ctx.student.parent_phone;
          const studentName = ctx.student.name || "Student";
          const batchName = ctx.batch?.name || "";

          // Renewal template (always receipt-only — gated upstream by
          // receipt existence, so receiptLink is guaranteed here).
          let msg;
          if (ctx.mode === "renewal") {
            msg =
`Hi,

${studentName}'s membership has been successfully renewed.

Payment Receipt:
${ctx.receiptLink}

Thank you.`;
          } else {
            // Enrollment / batch-change template — graceful: drops whichever
            // line is missing. Upstream guarantees ≥1 of the two links exists.
            const parts = [`Hi,\n\n${studentName} has been enrolled in the batch:\n\n${batchName}`];
            if (ctx.inviteLink) {
              parts.push(`Please join the WhatsApp group using the link below:\n\n${ctx.inviteLink}`);
            }
            if (ctx.receiptLink) {
              parts.push(`Payment Receipt:\n${ctx.receiptLink}`);
            }
            parts.push("Thank you.");
            msg = parts.join("\n\n");
          }
          openWhatsApp(phone, msg, { defaultCountryCode: default_country_code });
        }}
      />
    </div>
  );
};

export default StudentManagement;
